import React from 'react'
import styled from 'styled-components'

import { Network } from '@root/entities/network'
import { formatDurationShortLocalized } from '@root/shared'
import { AmountFormatted, PriceFormatted } from '@root/shared/core/format'
import { useTranslation } from '@root/shared/localization'

import { AnalysisAvgCostSoldValue } from './analysis-avg-cost-sold-value'
import { AnalysisBalanceValue } from './analysis-balance-value'
import { AnalysisCommon } from './analysis-common'
import { AnalysisHeaderValue } from './analysis-header-value'
import { AnalysisPnlValue } from './analysis-pnl-value'
import { AnalysisTooltip } from './analysis-tooltip'

import type { UserTradingAnalytics } from '../../models/user-trading-analytics'

export const Analysis = (props: {
  data: UserTradingAnalytics
  balanceType: 'usd' | 'native'
}) => {
  const { balanceType } = props
  const { analysis: data, network } = props.data
  const nativeCurrency = Network.toDisplayString(network).toUpperCase()
  const balanceCurrency = balanceType === 'usd' ? '$' : nativeCurrency

  const { t } = useTranslation()

  const rows = [
    {
      point: t('common.balance'),
      value: (
        <AnalysisBalanceValue
          native={
            <AmountFormatted
              amount={data.balance.native}
              currency={nativeCurrency}
              roundingMode="trunc"
            />
          }
          usd={
            <>
              (
              <AmountFormatted
                amount={data.balance.usd}
                currency="$"
                roundingMode="trunc"
              />
              )
            </>
          }
        />
      ),
    },
    {
      point: t('profile.analysis.avg-duration'),
      value: formatDurationShortLocalized(data.avgHold, t) || '—',
    },
    {
      point: t('common.cost'),
      value: (
        <PriceFormatted
          price={data.cost[balanceType]}
          currency={balanceCurrency}
        />
      ),
    },
    {
      point: (
        <span>
          <NoWrapText>{t('profile.analysis.avg-cost')}</NoWrapText> /{' '}
          <NoWrapText>{t('profile.analysis.avg-sold')}</NoWrapText>
        </span>
      ),
      value: (
        <AnalysisAvgCostSoldValue
          cost={
            <PriceFormatted
              price={data.avgCost[balanceType]}
              currency={balanceCurrency}
            />
          }
          sold={
            <PriceFormatted
              price={data.avgSold[balanceType]}
              currency={balanceCurrency}
            />
          }
        />
      ),
    },
    {
      point: t('profile.analysis.avg-realized-profits'),
      value: (
        <AnalysisPnlValue
          pnl={data.avgRealizedProfits[balanceType]}
          balanceType={balanceType}
          currencySign={balanceCurrency}
        />
      ),
    },
    {
      point: (
        <AnalysisTooltip content={t('profile.analysis.fees-tooltip')}>
          {t('profile.analysis.fees')}
        </AnalysisTooltip>
      ),
      value: (
        <AmountFormatted
          amount={data.fees[balanceType]}
          currency={balanceCurrency}
        />
      ),
    },
    {
      point: (
        <AnalysisTooltip content={t('profile.analysis.volume-tooltip')}>
          {t('common.volume')}
        </AnalysisTooltip>
      ),
      value: (
        <AmountFormatted
          amount={data.volume[balanceType]}
          currency={balanceCurrency}
        />
      ),
    },
    // {
    //   point: (
    //     <AnalysisTooltip content={t('profile.analysis.tracked')}>
    //       {t('profile.analysis.tracked')} / {t('profile.analysis.renamed')}
    //     </AnalysisTooltip>
    //   ),
    //   value: (
    //     <AnalysisTrackedRenamedValue
    //       tracked={data.trackedCount}
    //       renamed={data.renamedCount}
    //     />
    //   ),
    // },
  ]

  return (
    <AnalysisCommon
      value={
        <AnalysisHeaderValue buys={data.txs.buys} sells={data.txs.sells} />
      }
    >
      <AnalysisCommon.Main>
        {rows.map((row, index) => (
          <React.Fragment key={index}>
            <AnalysisCommon.Point>{row.point}</AnalysisCommon.Point>
            <AnalysisCommon.Value>{row.value}</AnalysisCommon.Value>
          </React.Fragment>
        ))}
      </AnalysisCommon.Main>
    </AnalysisCommon>
  )
}

const NoWrapText = styled.span`
  white-space: nowrap;
`
